import React, { useEffect, useState } from 'react';
import { Industry } from '../../../services/industryService';

interface IndustryFormProps {
  initialData?: Industry;
  onSubmit: (data: Industry) => void;
  onCancel: () => void;
}

const IndustryForm: React.FC<IndustryFormProps> = ({ initialData, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState<Omit<Industry, 'id'>>({
    name: initialData?.name || '',
    description: initialData?.description || '',
    longDescription: initialData?.longDescription || '',
    icon: initialData?.icon || '💼',
    metaTitle: initialData?.metaTitle || '',
    metaDescription: initialData?.metaDescription || '',
    expertise: initialData?.expertise || [],
    benefits: initialData?.benefits || [],
    challenges: initialData?.challenges || [],
    solutions: initialData?.solutions || [],
    stats: {
      clients: initialData?.stats.clients || 0,
      projectsCompleted: initialData?.stats.projectsCompleted || 0,
      successRate: initialData?.stats.successRate || '0%',
      roi: initialData?.stats.roi || '0%'
    },
    testimonials: initialData?.testimonials || [],
    faq: initialData?.faq || []
  });

  const [newExpertise, setNewExpertise] = useState('');
  const [newBenefit, setNewBenefit] = useState('');
  const [newChallenge, setNewChallenge] = useState('');
  const [newSolution, setNewSolution] = useState({
    name: '',
    description: '',
    benefits: [] as string[],
    technologies: [] as string[]
  });
  const [newSolutionBenefit, setNewSolutionBenefit] = useState('');
  const [newSolutionTechnology, setNewSolutionTechnology] = useState('');
  const [newTestimonial, setNewTestimonial] = useState({
    author: '',
    position: '',
    content: ''
  });
  const [newFaq, setNewFaq] = useState({
    question: '',
    answer: ''
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      id: initialData?.id || crypto.randomUUID(),
      ...formData
    });
  };

  const addExpertise = () => {
    if (newExpertise.trim()) {
      setFormData(prev => ({
        ...prev,
        expertise: [...prev.expertise, newExpertise.trim()]
      }));
      setNewExpertise('');
    }
  };

  const addBenefit = () => {
    if (newBenefit.trim()) {
      setFormData(prev => ({
        ...prev,
        benefits: [...prev.benefits, newBenefit.trim()]
      }));
      setNewBenefit('');
    }
  };

  const addChallenge = () => {
    if (newChallenge.trim()) {
      setFormData(prev => ({
        ...prev,
        challenges: [...prev.challenges, newChallenge.trim()]
      }));
      setNewChallenge('');
    }
  };

  const addSolutionBenefit = () => {
    if (newSolutionBenefit.trim()) {
      setNewSolution(prev => ({
        ...prev,
        benefits: [...prev.benefits, newSolutionBenefit.trim()]
      }));
      setNewSolutionBenefit('');
    }
  };

  const addSolutionTechnology = () => {
    if (newSolutionTechnology.trim()) {
      setNewSolution(prev => ({
        ...prev,
        technologies: [...prev.technologies, newSolutionTechnology.trim()]
      }));
      setNewSolutionTechnology('');
    }
  };

  const addSolution = () => {
    if (newSolution.name && newSolution.description) {
      setFormData(prev => ({
        ...prev,
        solutions: [...prev.solutions, { ...newSolution }]
      }));
      setNewSolution({
        name: '',
        description: '',
        benefits: [],
        technologies: []
      });
    }
  };

  const addTestimonial = () => {
    if (newTestimonial.author && newTestimonial.content) {
      setFormData(prev => ({
        ...prev,
        testimonials: [...prev.testimonials, { ...newTestimonial }]
      }));
      setNewTestimonial({
        author: '',
        position: '',
        content: ''
      });
    }
  };

  const addFaq = () => {
    if (newFaq.question && newFaq.answer) {
      setFormData(prev => ({
        ...prev,
        faq: [...prev.faq, { ...newFaq }]
      }));
      setNewFaq({
        question: '',
        answer: ''
      });
    }
  };

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onCancel();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onCancel]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/50 p-4"
      onClick={onCancel}
    >
      <div
        className="flex max-h-[92vh] w-full max-w-4xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="sticky top-0 z-10 flex items-center justify-between border-b bg-white px-6 py-4">
          <h3 className="text-xl font-semibold">
            {initialData ? 'Modifier le Métier' : 'Nouveau Métier'}
          </h3>
          <button
            type="button"
            onClick={onCancel}
            aria-label="Fermer la fenêtre métier"
            className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-gray-100 text-2xl leading-none text-gray-700 transition-colors hover:bg-gray-200"
          >
            ×
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-6 overflow-y-auto p-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <h4 className="font-semibold">Informations de base</h4>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nom
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neon-orange"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Icône
                </label>
                <input
                  type="text"
                  value={formData.icon}
                  onChange={(e) => setFormData(prev => ({ ...prev, icon: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neon-orange"
                  placeholder="Emoji (ex: 💼)"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description courte
              </label>
              <input
                type="text"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neon-orange"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description longue
              </label>
              <textarea
                value={formData.longDescription}
                onChange={(e) => setFormData(prev => ({ ...prev, longDescription: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neon-orange"
                rows={4}
                required
              />
            </div>
          </div>

          {/* SEO */}
          <div className="space-y-4">
            <h4 className="font-semibold">SEO</h4>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Meta Title
              </label>
              <input
                type="text"
                value={formData.metaTitle}
                onChange={(e) => setFormData(prev => ({ ...prev, metaTitle: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neon-orange"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Meta Description
              </label>
              <textarea
                value={formData.metaDescription}
                onChange={(e) => setFormData(prev => ({ ...prev, metaDescription: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neon-orange"
                rows={2}
                required
              />
            </div>
          </div>

          {/* Stats */}
          <div className="space-y-4">
            <h4 className="font-semibold">Statistiques</h4>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre de Clients
                </label>
                <input
                  type="number"
                  value={formData.stats.clients}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    stats: { ...prev.stats, clients: parseInt(e.target.value) }
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neon-orange"
                  min="0"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Projets Complétés
                </label>
                <input
                  type="number"
                  value={formData.stats.projectsCompleted}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    stats: { ...prev.stats, projectsCompleted: parseInt(e.target.value) }
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neon-orange"
                  min="0"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Taux de Satisfaction
                </label>
                <input
                  type="text"
                  value={formData.stats.successRate}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    stats: { ...prev.stats, successRate: e.target.value }
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neon-orange"
                  placeholder="ex: 95%"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  ROI
                </label>
                <input
                  type="text"
                  value={formData.stats.roi}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    stats: { ...prev.stats, roi: e.target.value }
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neon-orange"
                  placeholder="ex: 200%"
                  required
                />
              </div>
            </div>
          </div>

          {/* Challenges */}
          <div className="space-y-4">
            <h4 className="font-semibold">Défis</h4>
            
            <div className="flex gap-2">
              <input
                type="text"
                value={newChallenge}
                onChange={(e) => setNewChallenge(e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neon-orange"
                placeholder="Nouveau défi (ex: Télémédecine)"
              />
              <button
                type="button"
                onClick={addChallenge}
                className="px-4 py-2 bg-neon-orange text-white rounded-lg hover:bg-[#FF4500] transition-colors"
              >
                Ajouter
              </button>
            </div>

            <div className="space-y-2">
              {formData.challenges.map((item, index) => (
                <div key={index} className="flex justify-between items-center bg-gray-50 p-2 rounded-lg">
                  <span>{item}</span>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({
                      ...prev,
                      challenges: prev.challenges.filter((_, i) => i !== index)
                    }))}
                    className="text-red-500 hover:text-red-700"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Expertise */}
          <div className="space-y-4">
            <h4 className="font-semibold">Expertise</h4>
            
            <div className="flex gap-2">
              <input
                type="text"
                value={newExpertise}
                onChange={(e) => setNewExpertise(e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neon-orange"
                placeholder="Nouvelle expertise"
              />
              <button
                type="button"
                onClick={addExpertise}
                className="px-4 py-2 bg-neon-orange text-white rounded-lg hover:bg-[#FF4500] transition-colors"
              >
                Ajouter
              </button>
            </div>

            <div className="space-y-2">
              {formData.expertise.map((item, index) => (
                <div key={index} className="flex justify-between items-center bg-gray-50 p-2 rounded-lg">
                  <span>{item}</span>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({
                      ...prev,
                      expertise: prev.expertise.filter((_, i) => i !== index)
                    }))}
                    className="text-red-500 hover:text-red-700"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Benefits */}
          <div className="space-y-4">
            <h4 className="font-semibold">Bénéfices</h4>
            
            <div className="flex gap-2">
              <input
                type="text"
                value={newBenefit}
                onChange={(e) => setNewBenefit(e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neon-orange"
                placeholder="Nouveau bénéfice"
              />
              <button
                type="button"
                onClick={addBenefit}
                className="px-4 py-2 bg-neon-orange text-white rounded-lg hover:bg-[#FF4500] transition-colors"
              >
                Ajouter
              </button>
            </div>

            <div className="space-y-2">
              {formData.benefits.map((item, index) => (
                <div key={index} className="flex justify-between items-center bg-gray-50 p-2 rounded-lg">
                  <span>{item}</span>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({
                      ...prev,
                      benefits: prev.benefits.filter((_, i) => i !== index)
                    }))}
                    className="text-red-500 hover:text-red-700"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Solutions */}
          <div className="space-y-4">
            <h4 className="font-semibold">Solutions</h4>
            
            <div className="space-y-4 bg-gray-50 p-4 rounded-lg">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nom de la solution
                </label>
                <input
                  type="text"
                  value={newSolution.name}
                  onChange={(e) => setNewSolution(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neon-orange"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={newSolution.description}
                  onChange={(e) => setNewSolution(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neon-orange"
                  rows={2}
                />
              </div>

              {/* Solution Benefits */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Bénéfices
                </label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={newSolutionBenefit}
                    onChange={(e) => setNewSolutionBenefit(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neon-orange"
                    placeholder="Nouveau bénéfice"
                  />
                  <button
                    type="button"
                    onClick={addSolutionBenefit}
                    className="px-4 py-2 bg-neon-orange text-white rounded-lg hover:bg-[#FF4500] transition-colors"
                  >
                    +
                  </button>
                </div>
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {newSolution.benefits.map((benefit, index) => (
                    <div key={index} className="flex justify-between items-center bg-white p-2 rounded-lg">
                      <span>{benefit}</span>
                      <button
                        type="button"
                        onClick={() => setNewSolution(prev => ({
                          ...prev,
                          benefits: prev.benefits.filter((_, i) => i !== index)
                        }))}
                        className="text-red-500 hover:text-red-700"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Solution Technologies */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Technologies
                </label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={newSolutionTechnology}
                    onChange={(e) => setNewSolutionTechnology(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neon-orange"
                    placeholder="Nouvelle technologie"
                  />
                  <button
                    type="button"
                    onClick={addSolutionTechnology}
                    className="px-4 py-2 bg-neon-orange text-white rounded-lg hover:bg-[#FF4500] transition-colors"
                  >
                    +
                  </button>
                </div>
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {newSolution.technologies.map((tech, index) => (
                    <div key={index} className="flex justify-between items-center bg-white p-2 rounded-lg">
                      <span>{tech}</span>
                      <button
                        type="button"
                        onClick={() => setNewSolution(prev => ({
                          ...prev,
                          technologies: prev.technologies.filter((_, i) => i !== index)
                        }))}
                        className="text-red-500 hover:text-red-700"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <button
                type="button"
                onClick={addSolution}
                className="w-full px-4 py-2 bg-neon-orange text-white rounded-lg hover:bg-[#FF4500] transition-colors"
              >
                Ajouter la solution
              </button>
            </div>

            <div className="space-y-4">
              {formData.solutions.map((solution, index) => (
                <div key={index} className="bg-white p-4 rounded-lg border border-gray-200">
                  <div className="flex justify-between items-start mb-2">
                    <h5 className="font-semibold">{solution.name}</h5>
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({
                        ...prev,
                        solutions: prev.solutions.filter((_, i) => i !== index)
                      }))}
                      className="text-red-500 hover:text-red-700"
                    >
                      ×
                    </button>
                  </div>
                  <p className="text-gray-600 mb-2">{solution.description}</p>
                  <div className="text-sm">
                    <div className="mb-1">
                      <strong>Bénéfices:</strong> {solution.benefits.join(', ')}
                    </div>
                    <div>
                      <strong>Technologies:</strong> {solution.technologies.join(', ')}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Testimonials */}
          <div className="space-y-4">
            <h4 className="font-semibold">Témoignages</h4>
            
            <div className="space-y-4 bg-gray-50 p-4 rounded-lg">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Auteur
                </label>
                <input
                  type="text"
                  value={newTestimonial.author}
                  onChange={(e) => setNewTestimonial(prev => ({ ...prev, author: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neon-orange"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Poste
                </label>
                <input
                  type="text"
                  value={newTestimonial.position}
                  onChange={(e) => setNewTestimonial(prev => ({ ...prev, position: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neon-orange"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Témoignage
                </label>
                <textarea
                  value={newTestimonial.content}
                  onChange={(e) => setNewTestimonial(prev => ({ ...prev, content: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neon-orange"
                  rows={3}
                />
              </div>

              <button
                type="button"
                onClick={addTestimonial}
                className="w-full px-4 py-2 bg-neon-orange text-white rounded-lg hover:bg-[#FF4500] transition-colors"
              >
                Ajouter le témoignage
              </button>
            </div>

            <div className="space-y-4">
              {formData.testimonials.map((testimonial, index) => (
                <div key={index} className="bg-white p-4 rounded-lg border border-gray-200">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h5 className="font-semibold">{testimonial.author}</h5>
                      <p className="text-sm text-gray-500">{testimonial.position}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({
                        ...prev,
                        testimonials: prev.testimonials.filter((_, i) => i !== index)
                      }))}
                      className="text-red-500 hover:text-red-700"
                    >
                      ×
                    </button>
                  </div>
                  <p className="text-gray-600">{testimonial.content}</p>
                </div>
              ))}
            </div>
          </div>

          {/* FAQ */}
          <div className="space-y-4">
            <h4 className="font-semibold">FAQ</h4>
            
            <div className="space-y-4 bg-gray-50 p-4 rounded-lg">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Question
                </label>
                <input
                  type="text"
                  value={newFaq.question}
                  onChange={(e) => setNewFaq(prev => ({ ...prev, question: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neon-orange"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Réponse
                </label>
                <textarea
                  value={newFaq.answer}
                  onChange={(e) => setNewFaq(prev => ({ ...prev, answer: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neon-orange"
                  rows={3}
                />
              </div>

              <button
                type="button"
                onClick={addFaq}
                className="w-full px-4 py-2 bg-neon-orange text-white rounded-lg hover:bg-[#FF4500] transition-colors"
              >
                Ajouter la FAQ
              </button>
            </div>

            <div className="space-y-4">
              {formData.faq.map((faq, index) => (
                <div key={index} className="bg-white p-4 rounded-lg border border-gray-200">
                  <div className="flex justify-between items-start mb-2">
                    <h5 className="font-semibold">{faq.question}</h5>
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({
                        ...prev,
                        faq: prev.faq.filter((_, i) => i !== index)
                      }))}
                      className="text-red-500 hover:text-red-700"
                    >
                      ×
                    </button>
                  </div>
                  <p className="text-gray-600">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex gap-3 pt-4 border-t">
            <button
              type="submit"
              className="flex-1 bg-neon-orange text-white px-4 py-2 rounded-lg hover:bg-[#FF4500] transition-colors"
            >
              {initialData ? 'Mettre à jour' : 'Créer'}
            </button>
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Annuler
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default IndustryForm;
